import { useEffect, useState, useCallback } from 'react';
import { formatCurrencyInput } from '../utils/currencyUtils';
import { matchesOfferFilter } from '../utils/offerMode';

export const FILTER_SHEET_ANIM_MS = 220;

const CONDITION_RANKING = {
  'Damaged': 1,
  'Poor': 2,
  'Good': 3,
  'Very Good': 4,
  'Excellent': 5,
  'New': 6
};

const ACCESSORY_TYPES = new Set(['Power Supply', 'Pedalboard', 'Other Accessory']);

// Stable empty array used as default for `hiddenMatchIds` so callers that
// omit the param (SavedMatches, HiddenMatches) don't cause a new reference
// on every render — which would retrigger the apply-filters useEffect every
// cycle and cause an infinite "Maximum update depth exceeded" loop.
const EMPTY_HIDDEN = [];

/**
 * Extracted, page-agnostic filter state + derivation + apply pipeline for the
 * matches surface. Drop-in equivalent of the inline filter system that lived
 * in Matches.jsx prior to this extraction.
 *
 * Behavior is byte-for-byte the same as Matches.jsx pre-extraction:
 *   - Same seven filter dimensions (brands, models, effect types, price,
 *     condition min, decades, category).
 *   - Same multi-select chip semantics for brands/models/effect-types/decades.
 *   - Same single-select for condition min and category.
 *   - Same available-options derivation (matched-pedal-only, models scoped to
 *     selected brands).
 *   - Same prune-on-disappear behavior when matches change in real-time.
 *   - Same apply-filters pipeline (including the legacy
 *     hiddenMatchIds-stripping step, optional via param).
 *   - Same bottom-sheet open/close state machine (220ms animation).
 *   - Same activeFilterCount math.
 *   - Same defensive sessionStorage seed (Array.isArray / typeof guards).
 *
 * Scroll restoration is intentionally NOT handled here — that's page-specific
 * (only Matches.jsx wants it today). The hook returns the raw `savedState`
 * snapshot it read on mount so a page can use it for scroll restore against
 * the same predicate that produced its `scrollY`.
 *
 * @param {object} params
 * @param {Array} params.matches - Source array of match pairs. Each must have
 *   `myPedal` and `matchedPedal` (each with at minimum: id, make, model,
 *   effectTypes, price, condition, decade).
 * @param {Array<string>} [params.hiddenMatchIds=[]] - Optional list of
 *   `${myId}_${theirId}` composite ids to strip from the filtered output.
 *   Preserves the redundant hidden-strip step from the original Matches.jsx
 *   pipeline. Pages that have no hide concept (Saved, Hidden) omit this.
 * @param {string} params.storageKey - Per-page sessionStorage key. Used for
 *   the synchronous on-mount filter-state seed. Must be unique per page.
 */
export function useMatchFilters({ matches, hiddenMatchIds = EMPTY_HIDDEN, storageKey }) {
  // Synchronous session read used to seed filter state below so that when
  // the live `onSnapshot` matches arrive on remount, they get filtered
  // against the same predicate that produced the saved scroll position.
  // 30-min staleness window — matches LatestPedals / SearchResults.
  const savedState = (() => {
    if (!storageKey) return null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.timestamp > 30 * 60 * 1000) return null;
      return parsed;
    } catch { return null; }
  })();

  // Defensive sessionStorage restore: any missing/wrong-shape value falls back
  // to its empty default. Old saved state from before the filter sheet redesign
  // will simply reset to empty rather than crash the page.
  const s = savedState || {};
  const [filterBrands, setFilterBrands] = useState(Array.isArray(s.filterBrands) ? s.filterBrands : []);
  const [filterModels, setFilterModels] = useState(Array.isArray(s.filterModels) ? s.filterModels : []);
  const [filterEffectTypes, setFilterEffectTypes] = useState(Array.isArray(s.filterEffectTypes) ? s.filterEffectTypes : []);
  const [filterPrice, setFilterPrice] = useState({
    min: typeof s.filterPrice?.min === 'string' ? s.filterPrice.min : '',
    max: typeof s.filterPrice?.max === 'string' ? s.filterPrice.max : ''
  });
  const [filterConditionMin, setFilterConditionMin] = useState(typeof s.filterConditionMin === 'string' ? s.filterConditionMin : '');
  const [filterDecades, setFilterDecades] = useState(Array.isArray(s.filterDecades) ? s.filterDecades : []);
  const [filterOffers, setFilterOffers] = useState(Array.isArray(s.filterOffers) ? s.filterOffers : []); // 'Trade' / 'Buy' (plan §5)
  const [categoryFilter, setCategoryFilter] = useState(
    s.categoryFilter === 'pedals' || s.categoryFilter === 'accessories' ? s.categoryFilter : 'all'
  );

  const [availableBrands, setAvailableBrands] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableEffectTypes, setAvailableEffectTypes] = useState([]);
  const [availableDecades, setAvailableDecades] = useState([]);

  const [filteredMatches, setFilteredMatches] = useState([]);

  // ── Bottom-sheet state ────────────────────────────────────────────────
  // Mirrors the Browse / Recommendations bottom-sheet pattern.
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isFilterSheetClosing, setIsFilterSheetClosing] = useState(false);
  const [filterSubPage, setFilterSubPage] = useState(null);

  const closeFilterSheet = useCallback(() => {
    if (!isFilterSheetOpen || isFilterSheetClosing) return;
    setIsFilterSheetClosing(true);
    setTimeout(() => {
      setIsFilterSheetOpen(false);
      setIsFilterSheetClosing(false);
      setFilterSubPage(null);
    }, FILTER_SHEET_ANIM_MS);
  }, [isFilterSheetOpen, isFilterSheetClosing]);

  const openFilterSheet = useCallback(() => {
    setIsFilterSheetClosing(false);
    setIsFilterSheetOpen(true);
    setFilterSubPage(null);
  }, []);

  // Auto-close the sheet when the bottom-tab-bar add menu opens. Same
  // event the BottomTabBar dispatches on Matches today.
  useEffect(() => {
    const handleAddOpened = () => { if (isFilterSheetOpen) closeFilterSheet(); };
    window.addEventListener('bottomTabBarAddOpened', handleAddOpened);
    return () => window.removeEventListener('bottomTabBarAddOpened', handleAddOpened);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterSheetOpen]);

  // ── Available-options derivation ──────────────────────────────────────
  // Derive filter options from the currently-loaded matches, looking only at
  // the matched (counterparty) pedal — the page is about pedals available to
  // you, so we don't seed brand/model/effect-type/decade options from your own
  // listings.
  //
  // When a user-selected filter value disappears from the available set (e.g.
  // matches changed in real-time and the brand/model/etc. is no longer
  // represented), we auto-prune it so the UI never shows a "ghost" selection
  // that filters out everything.

  useEffect(() => {
    if (!matches || matches.length === 0) {
      setAvailableBrands([]);
      return;
    }
    const set = new Set();
    matches.forEach(m => { if (m.matchedPedal?.make) set.add(m.matchedPedal.make); });
    const list = Array.from(set).sort();
    setAvailableBrands(list);
    setFilterBrands(prev => prev.filter(b => list.includes(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  useEffect(() => {
    if (!matches || matches.length === 0) {
      setAvailableModels([]);
      return;
    }
    const selectedBrands = filterBrands.length > 0
      ? new Set(filterBrands.map(b => b.toLowerCase()))
      : null;
    const set = new Set();
    matches.forEach(m => {
      const make = m.matchedPedal?.make;
      const model = m.matchedPedal?.model;
      if (!model) return;
      if (selectedBrands && !selectedBrands.has((make || '').toLowerCase())) return;
      set.add(model);
    });
    const list = Array.from(set).sort();
    setAvailableModels(list);
    setFilterModels(prev => prev.filter(m => list.includes(m)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, filterBrands]);

  useEffect(() => {
    if (!matches || matches.length === 0) {
      setAvailableEffectTypes([]);
      return;
    }
    const set = new Set();
    matches.forEach(m => {
      const ets = m.matchedPedal?.effectTypes;
      if (Array.isArray(ets)) ets.forEach(et => { if (et) set.add(et); });
    });
    const list = Array.from(set).sort();
    setAvailableEffectTypes(list);
    setFilterEffectTypes(prev => prev.filter(et => list.includes(et)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  useEffect(() => {
    if (!matches || matches.length === 0) {
      setAvailableDecades([]);
      return;
    }
    const set = new Set();
    matches.forEach(m => { if (m.matchedPedal?.decade) set.add(m.matchedPedal.decade); });
    // Sort decades chronologically by stripping the trailing 's'
    const list = Array.from(set).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    setAvailableDecades(list);
    setFilterDecades(prev => prev.filter(d => list.includes(d)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  // ── Apply-filters pipeline ────────────────────────────────────────────
  // Verbatim port from Matches.jsx. The hidden-strip step is kept (optional
  // via hiddenMatchIds param) so behavior on Matches.jsx is byte-identical.
  // SavedMatches/HiddenMatches pass no hiddenMatchIds and that step no-ops.
  useEffect(() => {
    const applyFilters = () => {
      let updated = [...matches];

      if (hiddenMatchIds.length > 0) {
        updated = updated.filter(match => {
          const matchId = `${match.myPedal.id}_${match.matchedPedal.id}`;
          return !hiddenMatchIds.includes(matchId);
        });
      }

      // All content filters below operate on the matched (counterparty) pedal
      // only — the page is "pedals available to you," not "pedals that involve
      // a brand/model/etc." For mixed brand/model behavior, see the available
      // options derivation above which already restricts options to what the
      // counterparty side actually has.

      // Category: pedal vs accessory derived from effectTypes contents,
      // mirroring how Browse (LatestPedals) classifies listings. A listing is
      // an "accessory" if it has no real pedal effect types — only accessory
      // markers like Power Supply / Pedalboard / Other Accessory.
      //
      // Important: match docs created BEFORE the matchMaker.js effectTypes
      // field deploy won't have the field at all. We treat those as
      // unknown-category and let them through under any filter, so users
      // don't see a half-empty page until the data catches up. Newly-created
      // and refreshed matches will have effectTypes and filter correctly.
      if (categoryFilter !== 'all') {
        updated = updated.filter(({ matchedPedal }) => {
          const ets = matchedPedal?.effectTypes;
          if (!Array.isArray(ets)) return true;
          const hasPedalType = ets.some(t => !ACCESSORY_TYPES.has(t));
          const isPedal = hasPedalType;
          if (categoryFilter === 'pedals') return isPedal;
          if (categoryFilter === 'accessories') return !isPedal;
          return true;
        });
      }

      if (filterBrands.length > 0) {
        const set = new Set(filterBrands.map(b => b.toLowerCase()));
        updated = updated.filter(({ matchedPedal }) =>
          set.has((matchedPedal.make || '').toLowerCase())
        );
      }

      if (filterModels.length > 0) {
        const set = new Set(filterModels.map(m => m.toLowerCase()));
        updated = updated.filter(({ matchedPedal }) =>
          set.has((matchedPedal.model || '').toLowerCase())
        );
      }

      if (filterEffectTypes.length > 0) {
        const wanted = new Set(filterEffectTypes);
        updated = updated.filter(({ matchedPedal }) => {
          const ets = matchedPedal?.effectTypes;
          if (!Array.isArray(ets) || ets.length === 0) return false;
          return ets.some(et => wanted.has(et));
        });
      }

      if (filterPrice.min !== '') {
        const min = Number(filterPrice.min);
        if (!Number.isNaN(min)) {
          updated = updated.filter(({ matchedPedal }) => Number(matchedPedal.price) >= min);
        }
      }

      if (filterPrice.max !== '') {
        const max = Number(filterPrice.max);
        if (!Number.isNaN(max)) {
          updated = updated.filter(({ matchedPedal }) => Number(matchedPedal.price) <= max);
        }
      }

      if (filterConditionMin !== '') {
        const minRank = CONDITION_RANKING[filterConditionMin];
        if (minRank) {
          updated = updated.filter(({ matchedPedal }) => {
            const pedalRank = CONDITION_RANKING[matchedPedal.condition] || 0;
            return pedalRank >= minRank;
          });
        }
      }

      if (filterDecades.length > 0) {
        const set = new Set(filterDecades);
        updated = updated.filter(({ matchedPedal }) => set.has(matchedPedal.decade));
      }
      if (filterOffers.length > 0) {
        updated = updated.filter(({ matchedPedal }) => matchesOfferFilter(matchedPedal, filterOffers));
      }

      setFilteredMatches(updated);
    };

    applyFilters();
  }, [matches, hiddenMatchIds, categoryFilter, filterBrands, filterModels, filterEffectTypes, filterPrice, filterConditionMin, filterDecades, filterOffers]);

  // ── Convenience derivations ───────────────────────────────────────────

  const activeFilterCount =
    (filterBrands.length > 0 ? 1 : 0) +
    (filterModels.length > 0 ? 1 : 0) +
    (filterEffectTypes.length > 0 ? 1 : 0) +
    ((filterPrice.min !== '' || filterPrice.max !== '') ? 1 : 0) +
    (filterConditionMin !== '' ? 1 : 0) +
    (filterDecades.length > 0 ? 1 : 0) +
    (filterOffers.length > 0 ? 1 : 0) +
    (categoryFilter !== 'all' ? 1 : 0);

  // Reset all seven filter dimensions. Mirrors the inline "Clear All" handler
  // in Matches.jsx. Does NOT close the sheet — that's intentional and matches
  // existing UX.
  const clearAllFilters = useCallback(() => {
    setFilterBrands([]);
    setFilterModels([]);
    setFilterEffectTypes([]);
    setFilterPrice({ min: '', max: '' });
    setFilterConditionMin('');
    setFilterDecades([]);
    setFilterOffers([]);
    setCategoryFilter('all');
  }, []);

  // Returns the serializable filter state at call time. Pages that need to
  // persist filter+scroll snapshots to sessionStorage (Matches.jsx) call this
  // at click/unload time and merge it with their own scrollY.
  const getFilterStateSnapshot = useCallback(() => ({
    filterBrands,
    filterModels,
    filterEffectTypes,
    filterPrice,
    filterConditionMin,
    filterDecades,
    filterOffers,
    categoryFilter
  }), [filterBrands, filterModels, filterEffectTypes, filterPrice, filterConditionMin, filterDecades, filterOffers, categoryFilter]);

  return {
    filterBrands, setFilterBrands,
    filterModels, setFilterModels,
    filterEffectTypes, setFilterEffectTypes,
    filterPrice, setFilterPrice,
    filterConditionMin, setFilterConditionMin,
    filterDecades, setFilterDecades,
    filterOffers, setFilterOffers,
    categoryFilter, setCategoryFilter,

    availableBrands,
    availableModels,
    availableEffectTypes,
    availableDecades,

    filteredMatches,

    isFilterSheetOpen,
    isFilterSheetClosing,
    filterSubPage,
    setFilterSubPage,
    openFilterSheet,
    closeFilterSheet,

    activeFilterCount,
    clearAllFilters,
    getFilterStateSnapshot,

    // Raw on-mount snapshot for callers that want to do their own scroll-restore
    // (Matches.jsx). Saved/Hidden ignore this. Object shape: { filterBrands,
    // filterModels, filterEffectTypes, filterPrice, filterConditionMin,
    // filterDecades, categoryFilter, scrollY, timestamp } — or null if none.
    savedState
  };
}

export { formatCurrencyInput };
